import find from 'lodash/find';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import remove from 'lodash/remove';
import { Mapper } from 'modules/core';
import Utils from 'modules/core/services/utils';
import { resolveErrorMessage } from 'modules/errors/services';
import {
  postList,
  fetchList,
  fetchLists,
  patchList,
  deleteList,
  postShareList,
  addTalentToList as addTalent,
  removeTalentFromList as removeTalent,
} from './api';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

export const loadList = createAsyncThunk(
  'lists/getList',
  async ({ accountId, listId, accountType }) =>
    await fetchList(accountId, listId, accountType),
);
export const loadLists = createAsyncThunk(
  'lists/getLists',
  async ({ accountId, accountType }) =>
    await fetchLists(accountId, accountType),
);
export const createList = createAsyncThunk(
  'lists/create',
  async ({ accountId, listName, accountType }) =>
    await postList(accountId, listName, accountType),
);
export const updateList = createAsyncThunk(
  'lists/update',
  async ({ accountId, listId, listName, accountType }) =>
    await patchList(accountId, listId, listName, accountType),
);
export const removeList = createAsyncThunk(
  'lists/remove',
  async ({ accountId, listId, accountType }) =>
    await deleteList(accountId, listId, accountType),
);
export const addTalentToList = createAsyncThunk(
  'lists/add',
  async ({ accountId, listId, talentId, accountType }) =>
    await addTalent(accountId, listId, talentId, accountType),
);
export const removeTalentFromList = createAsyncThunk(
  'lists/removeTalent',
  async ({ accountId, listId, talentId, accountType }) =>
    await removeTalent(accountId, listId, talentId, accountType),
);
export const shareList = createAsyncThunk(
  'lists/shareList',
  async ({ accountId, submitValues, accountType }) =>
    await postShareList(accountId, submitValues, accountType),
);

const listSlice = createSlice({
  name: 'lists',
  initialState: {
    loading: false,
    sort: '-createdAt',
    lists: [],
    addTalentInProgress: false,
    filterData: null,
    error: null,
  },
  reducers: {
    setSort(state, action) {
      state.sort = action.payload;
    },
    setListsFilterData(state, action) {
      state.filterData = action.payload;
    },
    clearListsFilterData(state) {
      state.filterData = null;
    },
    clearListsError(state) {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadList.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadList.rejected, (state, action) => {
        state.loading = false;
        state.error = resolveErrorMessage(action.error);
      })
      .addCase(loadList.fulfilled, (state, action) => {
        state.loading = false;
        state.lists = Mapper.addOrReplace(state.lists, action.payload.data);
        state.error = null;
      })
      .addCase(loadLists.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadLists.rejected, (state, action) => {
        state.loading = false;
        state.error = resolveErrorMessage(action.error);
      })
      .addCase(loadLists.fulfilled, (state, action) => {
        state.loading = false;
        state.lists = Mapper.addOrReplaceArray(
          state.lists,
          action.payload.data,
        );
        state.error = null;
      })
      .addCase(createList.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(createList.rejected, (state, _) => {
        state.loading = false;
        // state.error = resolveErrorMessage(action.error);
      })
      .addCase(createList.fulfilled, (state, action) => {
        state.loading = false;
        state.lists = [...state.lists, action.payload.data];
      })
      .addCase(updateList.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(updateList.rejected, (state, _) => {
        state.loading = false;
        // state.error = resolveErrorMessage(action.error);
      })
      .addCase(updateList.fulfilled, (state, action) => {
        state.loading = false;
        state.lists = state.lists.map(list => {
          return list.id !== action.payload.data.id
            ? list
            : action.payload.data;
        });
      })
      .addCase(removeList.rejected, (state, _) => {
        state.loading = false;
        // state.error = resolveErrorMessage(action.error);
      })
      .addCase(removeList.fulfilled, (state, action) => {
        state.loading = false;
        state.loading = false;
        state.lists = state.lists.filter(
          list => list.id !== action.meta.arg.listId,
        );
      })
      .addCase(addTalentToList.pending, (state, _) => {
        state.addTalentInProgress = true;
      })
      .addCase(addTalentToList.fulfilled, (state, action) => {
        const listId = action.meta.arg.listId;
        const talentId = action.meta.arg.talentId;

        state.addTalentInProgress = false;

        state.lists = state.lists.map(list => {
          if (list.id !== listId) {
            return list;
          }

          list.talentsIds.push(talentId);

          return list;
        });
      })
      .addCase(removeTalentFromList.fulfilled, (state, action) => {
        const listId = action.meta.arg.listId;
        const talentId = action.meta.arg.talentId;

        state.lists = state.lists.map(list => {
          if (list.id !== listId) {
            return list;
          }

          remove(list.talentsIds, id => id === talentId);

          return list;
        });
      })
      .addCase(shareList.rejected, (state, action) => {
        state.error = resolveErrorMessage(action.error);
      })
      .addCase(shareList.fulfilled, (state, _) => {
        state.error = null;
      });
  },
  selectors: {
    getLists: state => state.lists,
    getListsError: state => state.error,
    getListsLoading: state => state.loading,
    getSort: state => state.sort,
    getAddTalentInProgress: state => state.addTalentInProgress,
    getListsFilterData: state => state.filterData,
  },
});

export const reducer = listSlice.reducer;
export const {
  setSort,
  setListsFilterData,
  clearListsError,
  clearListsFilterData,
} = listSlice.actions;
export const {
  getLists,
  getListsError,
  getListsLoading,
  getSort,
  getAddTalentInProgress,
  getListsFilterData,
} = listSlice.selectors;

export const getListsByAccountId = Utils.memoize((accountId, accountType) =>
  createSelector(
    getLists,
    lists => {
      if (accountType === 'producer') {
        return filter(lists, list => list.producerId === accountId);
      }
      if (accountType === 'agent') {
        const temp = filter(lists, list => list.agentId === accountId);
        return temp;
      }
      return [];
    },
  ),
);
export const getListById = id =>
  createSelector(
    getLists,
    lists => find(lists, list => list.id === id),
  );
export const getListsByTalentId = talentId =>
  createSelector(
    getLists,
    lists => filter(lists, list => includes(list.talentsIds, talentId)),
  );
